import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import DataTable from './Common/DataTable';
import Modal from './Common/Modal';
import { useAuth } from '../contexts/AuthContext';

const FORMATS = ['PDF', 'Word', 'Link', 'eBook'];

const EMPTY_FORM = {
  title: '',
  author: '',
  format: 'PDF',
  file_url: '',
  description: '',
  class_id: '',
  category: 'Academic',
};

export default function LibraryManagement() {
  const { user } = useAuth();
  const isTeacher = user?.role === 'teacher';
  const canEdit = ['admin', 'librarian', 'teacher'].includes(user?.role);
  const canDelete = ['admin', 'librarian'].includes(user?.role);

  const location = useLocation();
  const navigate = useNavigate();

  const [books, setBooks] = useState([]);
  const [classes, setClasses] = useState([]);
  const [myClass, setMyClass] = useState(null);   // for teachers
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterClass, setFilterClass] = useState('all');
  const [filterFormat, setFilterFormat] = useState('all');
  const [search, setSearch] = useState('');

  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => { fetchData(); }, []);

  // ─── Auto-open Add Book modal when navigated from a Quick Action ───
  useEffect(() => {
    if (location.state?.openAddBook) {
      setEditing(null);
      setForm(EMPTY_FORM);
      setModalOpen(true);

      // Clear the navigation state so refreshing / back-nav doesn't re-open
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const fetchData = async () => {
    setLoading(true);

    try {
      const b = await api.get('/library/');
      setBooks(b.data || []);
    } catch (err) { console.error('Library fetch failed:', err); }

    try {
      const c = await api.get('/classes/');
      setClasses(c.data || []);
    } catch (err) { console.error('Classes fetch failed:', err); }

    // For teachers: get their assigned class
    if (isTeacher) {
      try {
        const res = await api.get('/teacher/my-class');
        setMyClass(res.data);
      } catch (err) { console.error('Teacher class fetch failed:', err); }
    }

    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        class_id: form.class_id ? parseInt(form.class_id) : null,
      };
      // For teachers, class is auto-assigned by backend
      if (editing) {
        await api.put(`/library/${editing.id}`, payload);
      } else {
        await api.post('/library/', payload);
      }
      setModalOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      fetchData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this book?')) return;
    try {
      await api.delete(`/library/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openAddBook = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const filtered = books.filter(b => {
    if (filterClass !== 'all') {
      const cls = classes.find(c => c.id === parseInt(filterClass));
      if (cls && b.class_id !== cls.id) return false;
    }
    if (filterFormat !== 'all' && b.format !== filterFormat) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = `${b.title} ${b.author} ${b.category}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const getFormatBadge = (fmt) => {
    const colors = {
      'PDF': 'bg-red-100 text-red-800',
      'Word': 'bg-blue-100 text-blue-800',
      'Link': 'bg-purple-100 text-purple-800',
      'eBook': 'bg-green-100 text-green-800',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[fmt] || 'bg-gray-100 text-gray-800'}`}>
        {fmt}
      </span>
    );
  };

  const getClassName = (id) => {
    if (!id) return 'All Classes';
    const c = classes.find(x => x.id === id);
    return c ? c.name : '—';
  };

  const columns = ['Title', 'Author', 'Format', 'Class', 'Category', 'Actions'];
  const data = filtered.map(b => ({
    Title: (
      <div>
        <p className="font-medium">{b.title}</p>
        {b.description && <p className="text-xs text-gray-500 truncate max-w-xs">{b.description}</p>}
      </div>
    ),
    Author: b.author || '—',
    Format: getFormatBadge(b.format),
    Class: getClassName(b.class_id),
    Category: b.category || '—',
    Actions: (
      <div className="flex gap-2">
        {b.file_url && (
          <a
            href={b.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
          >
            <i className="fas fa-external-link-alt mr-1"></i> Open
          </a>
        )}
        {canEdit && (
          <button
            onClick={() => {
              setEditing(b);
              setForm({
                title: b.title,
                author: b.author || '',
                format: b.format,
                file_url: b.file_url || '',
                description: b.description || '',
                class_id: b.class_id || '',
                category: b.category || 'Academic',
              });
              setModalOpen(true);
            }}
            className="text-blue-600 hover:text-blue-800"
          >
            <i className="fas fa-edit"></i>
          </button>
        )}
        {canDelete && (
          <button
            onClick={() => handleDelete(b.id)}
            className="text-red-600 hover:text-red-800"
          >
            <i className="fas fa-trash"></i>
          </button>
        )}
      </div>
    ),
  }));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">📚 Library</h2>
        {canEdit && (
          <button
            onClick={openAddBook}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            <i className="fas fa-plus mr-2"></i> Add Book
          </button>
        )}
      </div>

      {isTeacher && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded mb-4">
          <p className="text-sm text-blue-700">
            <i className="fas fa-info-circle"></i>{' '}
            You can add reference books/links for your own class
            {myClass?.class_name && <strong> ({myClass.class_name})</strong>}.
            Class is auto-assigned. Only admins and librarians can delete books.
          </p>
        </div>
      )}

      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-white p-4 rounded shadow border-l-4 border-blue-500">
          <p className="text-xs text-gray-500"><i className="fas fa-book text-blue-500"></i> Total Books</p>
          <p className="text-2xl font-bold text-blue-600">{books.length}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-red-500">
          <p className="text-xs text-gray-500">📕 PDFs</p>
          <p className="text-2xl font-bold text-red-600">{books.filter(b => b.format === 'PDF').length}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-blue-400">
          <p className="text-xs text-gray-500">📘 Word Docs</p>
          <p className="text-2xl font-bold text-blue-700">{books.filter(b => b.format === 'Word').length}</p>
        </div>
        <div className="bg-white p-4 rounded shadow border-l-4 border-purple-500">
          <p className="text-xs text-gray-500">🔗 Links</p>
          <p className="text-2xl font-bold text-purple-600">{books.filter(b => b.format === 'Link').length}</p>
        </div>
      </div>

      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <select className="p-2 border rounded text-sm" value={filterClass}
          onChange={(e) => setFilterClass(e.target.value)}>
          <option value="all">All Classes</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <select className="p-2 border rounded text-sm" value={filterFormat}
          onChange={(e) => setFilterFormat(e.target.value)}>
          <option value="all">All Formats</option>
          {FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        <input type="text" placeholder="Search title, author, category..."
          className="flex-1 min-w-[200px] p-2 border rounded text-sm"
          value={search} onChange={(e) => setSearch(e.target.value)} />

        <span className="text-sm text-gray-500">
          Showing {filtered.length} of {books.length}
        </span>
      </div>

      <div className="bg-white p-4 rounded shadow">
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : data.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-book-open text-4xl mb-3"></i>
            <p>No books in the library yet.</p>
            {canEdit && <p className="text-sm mt-2">Click "Add Book" to add your first resource.</p>}
          </div>
        ) : (
          <DataTable columns={columns} data={data} />
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        title={editing ? 'Edit Book' : 'Add Book'}
        onSave={handleSubmit}
        saveLabel={editing ? 'Update' : 'Create'}
      >
        <form onSubmit={handleSubmit} className="space-y-3">
          <input className="w-full p-2 border rounded" placeholder="Book Title *"
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />

          <input className="w-full p-2 border rounded" placeholder="Author"
            value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />

          <div className="grid grid-cols-2 gap-3">
            <select className="w-full p-2 border rounded" value={form.format}
              onChange={(e) => setForm({ ...form, format: e.target.value })}>
              {FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>

            {isTeacher ? (
              <input
                className="w-full p-2 border rounded bg-gray-100 text-gray-600"
                value={myClass?.class_name ? `Auto: ${myClass.class_name}` : 'Auto: your class'}
                disabled
              />
            ) : (
              <select className="w-full p-2 border rounded" value={form.class_id}
                onChange={(e) => setForm({ ...form, class_id: e.target.value })}>
                <option value="">All Classes</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>

          <input className="w-full p-2 border rounded" placeholder="File URL / Link"
            value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} />

          <input className="w-full p-2 border rounded" placeholder="Category (e.g. Mathematics)"
            value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />

          <textarea className="w-full p-2 border rounded" rows="3" placeholder="Description"
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </form>
      </Modal>
    </div>
  );
}